import { Check } from 'lucide-react';

import { gradeLabel } from '@/components/features/mindspace/practice/utils';
import { useTransientFlag } from '@/hooks/ui/useTransientFlag';

interface RecentRatingFeedbackProps {
  /** Bumped each rating so the toast resets its visibility. */
  rateKey: number;
  grade: 1 | 2 | 3 | 4;
  /** Display string for next-review interval (e.g. "4 days"). */
  nextInterval: string;
  durationMs?: number;
}

/**
 * Small inline toast shown for ~600ms after a flashcard rating. Renders
 * the rating + a hint about when the system will surface this concept
 * again - makes FSRS visible-as-collaborator instead of opaque.
 *
 * Auto-hides via a timer keyed on `rateKey`; safe to leave mounted
 * permanently inside the runner.
 */
export function RecentRatingFeedback({
  rateKey,
  grade,
  nextInterval,
  durationMs = 600,
}: RecentRatingFeedbackProps) {
  // `rateKey <= 0` is the "no rating yet" sentinel, so the flag stays off
  // until the first real rating rather than flashing on mount.
  const visible = useTransientFlag(rateKey, durationMs, rateKey > 0);

  if (!visible) return null;

  const tone = toneClasses(grade);

  return (
    <div
      role='status'
      aria-live='polite'
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium motion-safe:duration-150 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 ${tone}`}
    >
      <Check className='h-3 w-3' />
      <span>{gradeLabel(grade)}</span>
      {/* Neither the separator nor the interval is faded: `opacity-80`
          measured 4.29:1 on the Good tier's tint. The grade label already
          leads by position; the interval does not need to recede as well. */}
      <span aria-hidden>·</span>
      <span>Next review in {nextInterval}</span>
    </div>
  );
}

function toneClasses(grade: 1 | 2 | 3 | 4): string {
  switch (grade) {
    case 4:
      return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
    case 3:
      return 'border-brand-200 bg-brand-50 text-brand-700 dark:border-primary dark:tint-brand';
    case 2:
      return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
    case 1:
      return 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/30 dark:text-rose-300';
  }
}

/**
 * Rough mapping from FSRS grade → human-readable next-review hint.
 * Conservative defaults that match the labels already shown on rate
 * buttons (Again: <1m, Hard: 6m, Good: 1d, Easy: 4d). Used when the
 * backend doesn't return a precise scheduled interval - the toast is a
 * UX-grade-of-truth, not a scheduling source.
 */
export function defaultIntervalLabel(grade: 1 | 2 | 3 | 4): string {
  switch (grade) {
    case 1:
      return '<1 min';
    case 2:
      return '~6 min';
    case 3:
      return '~1 day';
    case 4:
      return '~4 days';
  }
}
